import { DatePipe, NgIf, NgOptimizedImage } from '@angular/common';
import { Component, ElementRef, OnInit, inject, signal, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { SetupUserPasswordDto } from '@buspro/web/apis';
import { LocalUsersApi } from '@buspro/web/apis';
import { WEB_CONFIG, WebAppEnvironment, syncAutofilledInputs } from '@buspro/web/core';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzResultComponent } from 'ng-zorro-antd/result';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'admin-setup-password',
  templateUrl: './setup-local-account-password.component.html',
  styleUrls: ['./setup-local-account-password.component.scss'],
  imports: [
    NgOptimizedImage,
    NgIf,
    NzAlertComponent,
    ReactiveFormsModule,
    NzResultComponent,
    RouterLink,
    DatePipe,
    NzInputDirective,
  ],
  standalone: true,
})
export class SetupLocalAccountPasswordComponent implements OnInit {
  private fb = inject(FormBuilder);
  protected readonly environment = inject<WebAppEnvironment>(WEB_CONFIG);
  private route = inject(ActivatedRoute);
  private notification = inject(NzNotificationService);
  private localUsersApi = inject(LocalUsersApi);

  submitted$$ = new BehaviorSubject<number>(0);
  submitted = false;
  /// The submit button is no longer disabled while the form is invalid, so it
  /// needs its own in-flight state — otherwise a second tap fires a second
  /// account-setup request.
  isSubmitting = signal<boolean>(false);
  form = this.fb.group({
    email: [{ value: '', disabled: true }, [Validators.required]],
    password: ['', [Validators.required]],
    passwordConfirm: ['', [Validators.required]],
  });

  private accountFormRef = viewChild<ElementRef<HTMLFormElement>>('accountForm');

  userId!: string;
  name!: string;

  websiteUrl = this.environment.publicWebsiteUrl;
  date = new Date();

  ngOnInit(): void {
    const { uid, name, email } = this.route.snapshot.queryParams;

    if (!uid) {
      // Show error page
      this.notification.error('Ops! Something went wrong', 'Please contact support');
      return;
    }

    this.userId = uid;
    this.form.get('email')?.setValue(email);
    this.name = name;
  }

  completeAccountSetup() {
    // Adopt anything the browser filled in — a value assigned straight onto an
    // input never reaches the control, so the guard below would otherwise
    // reject a form whose fields are visibly complete.
    syncAutofilledInputs(this.form, this.accountFormRef()?.nativeElement);

    // Nothing validated this before: the disabled button was the only thing
    // standing between an empty form and a POST that would have set the
    // account's password to ''.
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notification.error('Password required', 'Please fill in both password fields to continue');
      return;
    }

    if (this.form.get('password')?.value !== this.form.get('passwordConfirm')?.value) {
      this.notification.error('Passwords does not match', 'Please check your password');
      return;
    }

    const payload: SetupUserPasswordDto = {
      userId: this.userId,
      password: this.form.get('password')!.value!,
    };

    this.isSubmitting.set(true);
    this.localUsersApi.localUsersControllerCompleteUserAccountSetup({ body: payload }).subscribe(
      () => {
        this.isSubmitting.set(false);
        this.submitted$$.next(1);
        this.submitted = true;
      },
      (error) => {
        this.isSubmitting.set(false);
        console.log(error);
        const note = {
          title: 'Ops! Something went wrong',
          content: error.error?.errorMessage,
        };
        this.notification.error(note.title, note.content);
      },
    );
  }
}
